/* ============================================================
   US-Parts24 · R472 — Sanftes Scrollen (Lenis) auf JEDER Seite.

   Vorher (R404) baute usp-diagnose.js sein eigenes Lenis, nur auf der
   Startseite. Drei Löcher, alle gemessen:
   1. Der Scroller wurde EINMAL beim Start bestimmt. Wer die Seite unter
      990 px öffnete (oder der Vorschau-Browser noch schmal war), bekam
      Lenis am Fenster — das Fenster scrollt auf dem Desktop aber nicht,
      `.page-wrapper` tut es. Ergebnis: Mausrad tot (scrollTop blieb 0).
   2. Bei offenem Menü/Warenkorb setzt das Theme `html[scroll-lock]`.
      Lenis scrollte den Wrapper trotzdem weiter — die Seite lief hinter
      dem offenen Menü davon.
   3. Verschachtelte Scrollflächen (Mega-Menü, Galerie) wurden vom
      Mausrad-Abfang überfahren.

   Jetzt: ein Modul, eine Instanz, `window.uspSmooth` für alle anderen
   (Diagnose-Schiene, Prüflauf). Wird nur auf Zeigergeräten gebaut, nie
   bei Bewegungsreduzierung, und die Leistungswache darf es abschalten.
   Fällt es aus, scrollt die Seite nativ — es versteckt nichts.
   ============================================================ */

import { getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';

const html = document.documentElement;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const feinerZeiger = window.matchMedia('(pointer: fine)');
const inEditor = Boolean(window.Shopify && window.Shopify.designMode);
const wurzel = document.querySelector('[data-usp-smooth]');
const libSrc = wurzel ? wurzel.dataset.lenis : '';
const lerp = Math.min(0.2, Math.max(0.04, (parseFloat(wurzel && wurzel.dataset.lerp) || 10) / 100));
/* Touch (Handy/Tablet) ist per Theme-Einstellung möglich, Standard AUS:
   Am 15.09. war es einen Nachmittag lang an — Marcel: „laggt extrem, Handy
   verbuggt". JavaScript-gesteuertes Wischen ist auf dem iPhone langsamer
   als das native Scrollen, und Safari zieht die Adressleiste nicht mehr ein.
   Die Diagnose-Effekte (Schiene, Chip, LEDs) brauchen Lenis nicht. */
const touchErlaubt = Boolean(wurzel) && wurzel.dataset.touch === 'true';

const zustand = { lenis: null, aus: false, geladen: false, ladeVersprechen: null };

function skript(src) {
  if (zustand.ladeVersprechen) return zustand.ladeVersprechen;
  zustand.ladeVersprechen = new Promise((resolve, reject) => {
    if (window.Lenis) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.dataset.uspLib = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`konnte nicht laden: ${src}`));
    document.head.appendChild(s);
  });
  return zustand.ladeVersprechen;
}

/** Sperre des Themes (Menü, Warenkorb, Suche offen) oder eigenes Modal offen? */
function gesperrt() {
  return html.hasAttribute('scroll-lock') || Boolean(document.querySelector('.usp-modal:not([hidden])'));
}

function erlaubt() {
  if (inEditor || !libSrc || zustand.aus) return false;
  if (reduceMotion.matches) return false;
  if (!feinerZeiger.matches && !touchErlaubt) return false;
  const stufe = html.dataset.uspLeistung;
  return stufe !== 'sparsam' && stufe !== 'mittel';
}

function headerAbstand() {
  const v = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'))
    || parseFloat(getComputedStyle(html).getPropertyValue('--header-height'));
  return Number.isFinite(v) ? v : 0;
}

function bauen() {
  if (zustand.lenis || !window.Lenis || !erlaubt()) return;
  const desktop = scrollContainerMediaQuery.matches;
  const container = desktop ? getScrollContainer() : null;
  const touch = !feinerZeiger.matches;
  const opts = {
    lerp,
    wheelMultiplier: 1,
    smoothWheel: true,
    /* Touch: das Wischen selbst übernehmen, mit kurzem Nachlauf; Trägheit
       etwas straffer als am Rad, sonst „schwimmt" die Seite unterm Daumen. */
    syncTouch: touch,
    syncTouchLerp: 0.09,
    touchMultiplier: 1,
    touchInertiaExponent: 1.7,
    autoRaf: true,
    /* ⚠️ Performance (Marcel, 15.09.: „laggt alles überall extrem"):
       · kein `naiveDimensions` — das las bei JEDEM Bild die Scrollhöhe und
         zwang den Browser zum Layout; stattdessen beobachtet Lenis <main>
         (das wächst mit nachladendem Inhalt) und wird nach `load` einmal
         nachgemessen.
       · kein `allowNestedScroll` — das lief bei jedem Rad-Tick per
         getComputedStyle über den ganzen Ereignispfad. Verschachtelte
         Scrollflächen melden sich mit data-lenis-prevent / <dialog>. */
    allowNestedScroll: false,
    anchors: false, // eigene Behandlung mit Kopf-Abstand, siehe unten
    prevent: (node) =>
      gesperrt() ||
      Boolean(node && node.closest && node.closest('[data-lenis-prevent], dialog, .usp-modal, [data-usp-request-modal]')),
  };
  if (container && container !== document.scrollingElement && container !== html && container !== document.body) {
    opts.wrapper = container;
    /* Lenis 1.3 misst bei eigenem Wrapper dessen scrollHeight — `content`
       dient nur dem ResizeObserver. <main> wächst mit dem Inhalt, der
       Wrapper selbst (100dvh) nie. */
    opts.content = container.querySelector('main') || container;
  }
  const lenis = new window.Lenis(opts);
  zustand.lenis = lenis;
  /* Nach dem Laden (Bilder, Schriften, App-Blöcke) einmal frisch messen. */
  if (document.readyState === 'complete') setTimeout(() => lenis.resize(), 300);
  else window.addEventListener('load', () => setTimeout(() => lenis.resize(), 300), { once: true });
  document.addEventListener('shopify:section:load', () => lenis.resize());

  /* ⚠️ R485 — der abgeschnittene Fußbereich. Gemessen am 17.09. auf der
     Startseite: Lenis kannte als Grenze 10.334 px, der Wrapper konnte aber
     bis 10.710 px. Die letzten 376 px — der Footer — waren mit dem Rad
     NICHT erreichbar.

     Ursache: Oben steht `opts.content = <main>`, und daran hängt der
     ResizeObserver von Lenis. Im Wrapper liegen aber noch zwei Geschwister
     NEBEN <main>:  div.usp-sysstatus (56 px)  und  <footer> (546 px).
     Wächst einer davon nach dem Start — Schriften, Newsletter-Block,
     App-Embeds —, ändert sich <main> nicht, Lenis misst nicht nach, und die
     Grenze bleibt zu klein. <main> zu beobachten ist richtig, aber es ist
     eben nicht alles, was scrollt.

     Deshalb: JEDES direkte Kind des Wrappers beobachten. Das sind fünf
     Elemente, kostet also nichts, und deckt auch künftige Geschwister ab. */
  if (container && typeof ResizeObserver === 'function') {
    let geplant = 0;
    const nachmessen = () => {
      clearTimeout(geplant);
      geplant = setTimeout(() => {
        if (!zustand.lenis) return;
        const echt = container.scrollHeight - container.clientHeight;
        if (Math.abs(echt - zustand.lenis.limit) > 1) zustand.lenis.resize();
      }, 150);
    };
    const beobachter = new ResizeObserver(nachmessen);
    Array.from(container.children).forEach((kind) => beobachter.observe(kind));
    zustand.groessenBeobachter = beobachter;
    /* ScrollTrigger schiebt beim Anheften Platz ein (pinSpacing) — das ändert
       die Höhe, ohne dass ein Kind des Wrappers seine eigene Größe ändert. */
    document.addEventListener('usp:motion-refresh', nachmessen);
  }

  /* base.css gibt dem Wrapper `scroll-behavior: smooth`; Lenis setzt seine
     Klasse `lenis-smooth` nur WÄHREND es scrollt. Ein nativer Ankersprung im
     Ruhezustand liefe also als zweite Animation neben Lenis her (gemessen:
     Ziel 5.379 px, Ende bei 505). Deshalb eigene Klasse, immer. */
  lenis.rootElement.classList.add('usp-smooth-on');
  lenis.on('scroll', (l) => {
    document.dispatchEvent(new CustomEvent('usp:smooth-scroll', { detail: l }));
  });
  sperreSpiegeln();
  document.dispatchEvent(new CustomEvent('usp:smooth-ready'));
}

function zerstoeren() {
  if (!zustand.lenis) return;
  try { zustand.lenis.rootElement.classList.remove('usp-smooth-on'); } catch (_) { /* egal */ }
  try { zustand.lenis.destroy(); } catch (_) { /* egal */ }
  zustand.lenis = null;
  document.dispatchEvent(new CustomEvent('usp:smooth-off'));
}

async function starten() {
  if (!erlaubt()) return;
  try { await skript(libSrc); } catch (_) { return; }
  bauen();
}

/* ------------------------------------------------------------
   Sperre des Themes spiegeln: Menü auf → Lenis steht, Menü zu → läuft.
   ------------------------------------------------------------ */
/* ⚠️ Nur umschalten, wenn sich der Zustand wirklich ändert: `start()` setzt
   in Lenis die laufende Animation zurück (reset → Ziel = aktuelle Lage).
   Gemessen: Klick auf #kategorien → 60 ms später start() → Ziel 5.185 wurde
   zu 252, die Seite blieb dort stehen. */
function sperreSpiegeln() {
  const l = zustand.lenis;
  if (!l) return;
  if (gesperrt()) { if (!l.isStopped) l.stop(); }
  else if (l.isStopped) l.start();
}
new MutationObserver(sperreSpiegeln).observe(html, { attributes: true, attributeFilter: ['scroll-lock'] });

/* Das Anfrage-Modal setzt kein scroll-lock, sondern sein `hidden`-Attribut —
   gemessen: nach Schließen per Escape blieb Lenis gestoppt, weil nur der
   Klick beobachtet wurde. Deshalb das Attribut selbst beobachten. */
function modalBeobachten() {
  document.querySelectorAll('.usp-modal').forEach((m) => {
    new MutationObserver(sperreSpiegeln).observe(m, { attributes: true, attributeFilter: ['hidden'] });
  });
  document.addEventListener('click', () => setTimeout(sperreSpiegeln, 60), { passive: true });
  document.addEventListener('keydown', () => setTimeout(sperreSpiegeln, 60), { passive: true });
}
modalBeobachten();

/* Scroller wechselt (Fenster ↔ .page-wrapper bei 990 px): neu bauen. */
scrollContainerMediaQuery.addEventListener('change', () => { zerstoeren(); starten(); });
reduceMotion.addEventListener('change', () => { if (reduceMotion.matches) zerstoeren(); else starten(); });

/* ------------------------------------------------------------
   Anker-Links: sanft und mit Platz für den Kopf, statt Sprung.
   ------------------------------------------------------------ */
document.addEventListener('click', (e) => {
  const l = zustand.lenis;
  if (!l || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey) return;
  const a = e.target.closest && e.target.closest('a[href*="#"]');
  if (!a) return;
  let url;
  try { url = new URL(a.href, location.href); } catch (_) { return; }
  if (url.origin !== location.origin || url.pathname !== location.pathname || !url.hash || url.hash === '#') return;
  let ziel = null;
  try { ziel = document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch (_) { ziel = null; }
  if (!ziel) return;
  e.preventDefault();
  scrollTo(ziel);
  /* Pfad + Suchparameter behalten (z. B. ?variant= auf Produktseiten), nur den Anker anhängen */
  try { history.pushState(null, '', location.pathname + location.search + url.hash); } catch (_) { /* egal */ }
}, { capture: true }); // vor allen anderen Klick-Handlern, die den Sprung selbst auslösen könnten

function scrollTo(ziel, extra) {
  const l = zustand.lenis;
  const el = typeof ziel === 'string' ? document.querySelector(ziel) : ziel;
  if (!el) return;
  if (l) {
    l.scrollTo(el, Object.assign({ offset: -Math.round(headerAbstand()) - 8, force: false }, extra || {}));
  } else {
    el.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
  }
}

window.uspSmooth = {
  get lenis() { return zustand.lenis; },
  aktiv: () => Boolean(zustand.lenis),
  scrollTo,
  stop: () => { if (zustand.lenis && !zustand.lenis.isStopped) zustand.lenis.stop(); },
  start: () => { if (zustand.lenis && zustand.lenis.isStopped && !gesperrt()) zustand.lenis.start(); },
  /** Leistungswache: dauerhaft aus (bis `an()`). */
  aus: () => { zustand.aus = true; zerstoeren(); },
  an: () => { zustand.aus = false; starten(); },
};

/* R508 — Lenis erst nach dem Laden starten, im Leerlauf, und nie mitten in
   einer Scrollbewegung. Gemessen am 18.09. (sofort nach dem Laden gescrollt,
   CPU 4× gedrosselt): 106 Takte über 50 ms. Lenis bewegt die Seite per
   JavaScript — ist der Hauptfaden beim Laden beschäftigt (Apps, Schriften,
   Bilder), steht das GANZE Scrollen. Bis Lenis läuft, scrollt der Browser
   selbst; das bleibt auch unter Last flüssig. */
let zuletztGescrollt = 0;
document.addEventListener('scroll', () => { zuletztGescrollt = performance.now(); }, { passive: true, capture: true });
function spaeterStarten() {
  const leerlauf = window.requestIdleCallback || ((f) => setTimeout(f, 200));
  const versuch = () => {
    if (performance.now() - zuletztGescrollt < 450) { setTimeout(versuch, 300); return; }
    starten();
  };
  const los = () => setTimeout(() => leerlauf(versuch, { timeout: 2500 }), 800);
  if (document.readyState === 'complete') los();
  else window.addEventListener('load', los, { once: true });
}
spaeterStarten();
